import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Icon, Pagination, Status } from '@components'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import SearchInput from '@components/SearchInput'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { successMessages } from '@constants/feedbackMessages'
import { PencilAltIcon, TrashIcon } from '@heroicons/react/outline'
import { clientApi } from '@services/ClientApi'
import { loadingModal, openAlertModal, openModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import { formatCpfCnpj } from '@utils/helpers/CpfCnpjFormated'
import { useFilters, useInputBounce, useQuery } from '@utils/hooks'

import AddClient from './AddClient'
import EditClient from './EditClient'

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] mt-[32px] items-end flex-wrap',
	tag: 'font-medium text-blue-600 underline underline-offset-2',
	divider: 'mt-[32px] mb-[12px]',
	avatar: 'rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]',
}

function ListClients() {
	const [notErrorImg, setNotErrorImg] = useState<boolean>(true)

	const dispatch = useAppDispatch()
	const query = useQuery()
	const navigate = useNavigate()

	const pageQuery = Number(query.get('page')) || 1
	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))
	const [page, setPage] = useState(pageQuery)

	const { data, isLoading } = clientApi.useListQuery({
		page,
		search: encodeURIComponent(searchQuery),
	})

	const [deleteClient, { isLoading: loadingDelete, isSuccess: deleteSuccess }] =
		clientApi.useDeleteMutation()

	const searchCallback = useCallback(
		(value: string) => {
			navigate('/clientes?page=1')
			setSearchQuery(value)
		},
		[navigate]
	)

	useEffect(() => {
		dispatch(loadingModal({ loading: loadingDelete }))

		if (deleteSuccess) {
			dispatch(notificationCreators.show(successMessages.deleteClient))
		}
	}, [dispatch, loadingDelete, deleteSuccess])

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useInputBounce(search, searchCallback)

	useFilters({
		search: searchQuery,
		fallbackUrl: '/clientes',
	})

	return (
		<>
			<Flex className={styles.searchContainer}>
				<SearchInput
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className='flex-1'
					placeholder='Buscar Cliente'
				/>

				<Button onClick={() => dispatch(openModal({ name: 'addClient' }))}>
					Adicionar Cliente
				</Button>
			</Flex>

			{isLoading ? (
				<div className='flex justify-center'>
					<Spinner size='md' />
				</div>
			) : (
				<Table
					th={[
						{
							label: 'nome',
						},
						{ label: 'e-mail' },
						{ label: 'CPF/CNPJ' },
						{
							label: 'Status',
						},
						{ label: 'ações' },
					]}
				>
					{data?.data && data?.data?.length > 0 ? (
						data?.data.map(
							({ id: clientId, name, email, imageUrl, meta, cpfCnpj }) => (
								<Tr key={clientId}>
									<Td>
										<Link to={`/clientes/${clientId}`}>
											<Flex className='items-center gap-4'>
												{imageUrl && notErrorImg ? (
													<img
														src={imageUrl}
														alt={name}
														className=' h-11 w-11 rounded-full'
														onError={() => setNotErrorImg(false)}
													/>
												) : (
													<Icon
														name='avatar-default'
														className='w-11 h-11 rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]'
													/>
												)}
												{name}
											</Flex>
										</Link>
									</Td>

									<Td>
										<Link to={`/clientes/${clientId}`}>
											<Flex className='items-center gap-4'>{email}</Flex>
										</Link>
									</Td>
									<Td>{cpfCnpj && formatCpfCnpj(cpfCnpj)}</Td>
									<Td>
										<Status
											className='px-4 py-0'
											status={meta.isActive ? 'success' : 'danger'}
										>
											{meta.isActive ? 'Ativo' : 'Inativo'}
										</Status>
									</Td>
									<Td>
										<Flex className='items-center gap-4'>
											<Button
												className='!px-2 !py-2'
												leftIcon={<PencilAltIcon />}
												disabled={false}
												variant='outline'
												onClick={() =>
													dispatch(
														openModal({
															name: 'editClient',
															modalInfo: clientId,
														})
													)
												}
											>
												<></>
											</Button>
											<Button
												className='!px-2 !py-2'
												leftIcon={<TrashIcon />}
												disabled={false}
												variant='outline'
												onClick={() => {
													const removeClient = {
														title: 'Remover Cliente',
														message:
															'Tem certeza que deseja remover o cliente?',
														buttonLabel: 'Remover Cliente',
														onClick: () =>
															deleteClient({
																id: clientId,
															}),
													}

													dispatch(openAlertModal(removeClient))
												}}
											>
												<></>
											</Button>
										</Flex>
									</Td>
								</Tr>
							)
						)
					) : (
						<EmptyRow tdAmount={3} message='Nenhum cliente encontrado' />
					)}
				</Table>
			)}

			<Divider className={styles.divider} />
			{data?.data && data?.data?.length > 0 && (
				<Pagination
					pageCount={data?.meta.lastPage || 0}
					maxAmount={data?.meta.total || 0}
					perPage={data?.meta.perPage || 0}
				/>
			)}
			<AddClient />
			<EditClient />
		</>
	)
}

export default ListClients
